/**
 * Excel / CSV import-export helpers for module list pages.
 *
 * Uses the `xlsx` package that's already a dependency of `@kss/ui`.
 * The frontend never needs to bundle a separate CSV parser.
 */
import * as XLSX from 'xlsx';

export interface BulkColumnSpec<T> {
  /** Object key (matching the entity's create payload). */
  key: keyof T & string;
  /** Header label rendered in the spreadsheet. */
  header: string;
  /** Optional sample value placed in the template's first row. */
  sample?: string | number | boolean;
}

/**
 * Trigger a download of an empty `.xlsx` template containing only the
 * header row (and optionally a single sample row).
 */
export function downloadTemplate<T>(filename: string, columns: BulkColumnSpec<T>[]) {
  const headers = columns.map((c) => c.header);
  const sampleRow = columns.map((c) => c.sample ?? '');
  const sheet = XLSX.utils.aoa_to_sheet([headers, sampleRow]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheet, 'Template');
  XLSX.writeFile(wb, ensureXlsx(filename));
}

/**
 * Trigger a download of the given rows as `.xlsx`.
 */
export function downloadXlsx<T extends Record<string, unknown>>(
  filename: string,
  columns: BulkColumnSpec<T>[],
  rows: T[]
) {
  const data = rows.map((r) => {
    const out: Record<string, unknown> = {};
    for (const c of columns) out[c.header] = r[c.key] ?? '';
    return out;
  });
  const sheet = XLSX.utils.json_to_sheet(data, {
    header: columns.map((c) => c.header),
  });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheet, 'Data');
  XLSX.writeFile(wb, ensureXlsx(filename));
}

/**
 * Open a `<input type=file>` programmatically and read the selected
 * `.xlsx` / `.csv` file. Returns parsed rows mapped against `columns`.
 */
export function pickAndParseXlsx<T>(columns: BulkColumnSpec<T>[]): Promise<Partial<T>[]> {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,.xls,.csv';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) {
        resolve([]);
        return;
      }
      try {
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        const sheetName = wb.SheetNames[0];
        const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(wb.Sheets[sheetName]);
        const headerToKey = new Map(columns.map((c) => [c.header, c.key as string]));
        const rows: Partial<T>[] = json.map((raw) => {
          const out: Record<string, unknown> = {};
          for (const [header, value] of Object.entries(raw)) {
            const key = headerToKey.get(header);
            if (key) out[key] = value;
          }
          return out as Partial<T>;
        });
        resolve(rows);
      } catch (err) {
        reject(err);
      }
    };
    input.click();
  });
}

function ensureXlsx(name: string): string {
  return /\.xlsx$/i.test(name) ? name : `${name}.xlsx`;
}
